'use strict';

/**
 * Analytics Service
 *
 * TODO: The web segment.io does not support bulk uploads. And the node version
 * does not seem to work on the web, so we will need to create an Angular specific
 * class to drop in place of these libraries. This will also have the advantage
 * of being much smaller because we can make use of Angular $http.
 */

var _ = require('lodash');
var utilFormat = require('fast-format');
var SegmentIO = require('./lib/SegmentIO');

function Analytics ($injector, $q, $window, configPromise) {

  var queued = [];
  var segmentio = null;

  this.userId = '';
  this.traits = null;
  this.context = null;

  this.configured = configPromise.then(function (config) {
    var Clazz = $injector.invoke(SegmentIO);
    segmentio = new Clazz(config.segmentIO.writeKey, config.segmentIO.options);
    return config;
  });

  this.identified = (function () {
    var deferred = $q.defer();
    return deferred;
  }.bind(this))();

  this.ready = (function () {
    var promises = [];
    promises.push(this.configured, this.identified.promise);
    return $q.all(promises);
  }.bind(this))();

  // Flush any queued `events` when ready
  this.ready.then(function () {
    var _this = this;
    if (queued) {
      queued.forEach(function (obj) {
        var args = obj.args;
        var method = obj.method;
        _this[method].apply(_this, args);
      });
    }
  }.bind(this));

  /**
   * Immediately flush all batched events
   * @returns {promise} flush promise
   */
  this.flush = function() {
    var didExit = false;

    // force resolve if there is a user login error
    _.delay(function() {
      if(!didExit) this.identified.resolve();
    }.bind(this), 1000);

    return this.ready.then(function() {
      didExit = true;
      return segmentio.flush();
    });
  };

  /**
   * Sets the user identify data. This should be triggered as soon as the app starts.
   * The object is formatted to look like the one that is created by `segment.io`
   *
   * @param  {String} id User unique identifier. For commander this is the
   * something like a machineID, but for web this will be the userID.
   * @param  {Object} traits Additional useful user data
   * @param  {Object} context Additional useful information not directly attached
   * to the user such as the `version` of the application being used, which is
   * either a commit hash or an actual version number, or the `system` and/or
   * `browser` information
   * @returns {null} null
   */
  this.identify = function (id, traits, context, integrations) {
    this.userId = id;
    this.traits = traits || {};
    this.context = context || {};
    if(!this.context.adminSession) {
      this.configured.then(function () {
        segmentio.identify({
          userId: this.userId,
          traits: this.traits,
          context: this.context,
          integrations: integrations
        });
        this.identified.resolve();
      }.bind(this));
    } else {
      console.warn('Muting Tracking: Reason - AdminSession');
    }
  };

  /**
   * Begin tracking the duration the user has been using the application
   * @returns {null} null
   */
  this.trackSessionDuration = function () {
    // TODO
  };

  /**
   * Retrieve the current session duration for the user
   *
   * @returns {Number} Session duration in minutes
   */
  this.getSessionDuration = function () {
    // TODO
  };

  /**
   * Modify the identify context
   *
   * @param  {Object} context Will be merged with existing context
   * @returns {Object} A copy of the new context
   */
  this.setContext = function (context) {
    return this.ready.then(function () {
      _.extend(this.context, context);
      this.identify(this.userId, this.traits, this.context);
      return _.clone(this.context, true);
    }.bind(this));
  };

  /**
   * Modify the identify traits
   *
   * @param  {Object} traits Will be merged with existing traints
   * @returns {Object} A copy of the new traits
   */
  this.modifyTraits = function (traits) {
    return this.ready.then(function () {
      _.extend(this.traits, traits);
      this.identify(this.userId, this.traits, this.context);
      return _.clone(this.traits, true);
    }.bind(this));

  };

  /**
   * Method used to send a tracking event to Keen
   *
   * TODO: If user is offline or logging fails write to localstorage OR IndexedDB
   * @param  {string} category general category name
   * @param  {string} eventname the event name
   * @param  {object} eventdata analytics data to send with the event
   * @returns {null} null
   */
  this.track = function (category, eventname, eventdata, integrations) {
    return this.ready.then(function () {
      return new Promise(function(resolve, reject) {
        var collectionName = this.getEventCollectionName(category, eventname);

        eventdata.email = this.traits && this.traits.hasOwnProperty('email') ? this.traits.email : '';
        eventdata.name = this.traits && this.traits.hasOwnProperty('name') ? this.traits.name : '';

        // empty objects cause 400 errors when sending the events to Intercom, so remove them
        for (var key in this.context) {
          if (this.context.hasOwnProperty(key) && _.isEmpty(this.context[key])) {
            delete this.context[key];
          }
        }

        integrations = integrations ? integrations : {};

        if (!this.context.adminSession) {
          if (window.fbq) {
            window.fbq('trackCustom', collectionName, eventdata);
          }

          segmentio.track({
            userId: this.userId,
            event: collectionName,
            properties: eventdata, // ONLY this gets sent to Keen
            context: this.context, // this will NOT get sent to Keen... BUT does segmentIO use it?
            integrations: integrations
          }, function(err, res) {
            console.info('TRACK', collectionName, eventdata);
            if (err) {
              reject(err);
            } else {
              resolve(res);
            }
          });
        } else {
          console.warn('Muting Tracking: Reason - AdminSession');
          resolve();
        }
      }.bind(this))
    }.bind(this));
  };

  this.page = function (pagedata) {
    this.ready.then(function () {
      return new Promise(function(resolve, reject) {

        var data = pagedata || {};
        data = _.extend({
          traits: this.traits,
          userId: this.userId
        });

        data.context = _.extend(pagedata, this.context);

        data.properties = data.context;
        if(!this.context.adminSession) {
          segmentio.page(data, function(err, res) {
            console.info('PAGE', pagedata.title, pagedata);
            if (err) {
              reject(err);
            } else {
              resolve(res);
            }
          });
        }
        else {
          console.warn('Muting Tracking: Reason - AdminSession');
          resolve();
        }
      }.bind(this))
    }.bind(this));
  };

  /**
   * Retrieve the event collection name
   *
   * @param  {String} category generic name
   * @param  {String} eventname specific name
   * @returns {String} name
   */
  this.getEventCollectionName = function (category, eventname) {
    var format = '%s: %s';
    return utilFormat(format, _.capitalize(category), _.capitalize(eventname));
  };
}

module.exports = Analytics;



// WEBPACK FOOTER //
// ./~/@skycatch/skydash-saturn/boosters/analytics/src/Analytics.js